import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, ArrowLeft, Users, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import { useFilters } from "@/hooks/useFilters";
import inventory from "@/api/inventory";
import InventoryKPICards from "@/components/inventory/InventoryKPICards";
import InventorySummaryTable from "@/components/inventory/InventorySummaryTable";
import InventoryMonthlyPivotTable from "@/components/inventory/InventoryMonthlyPivotTable";
import InventoryDeadStockTable from "@/components/inventory/InventoryDeadStockTable";
import InventoryUserMovementsTable from "@/components/inventory/InventoryUserMovementsTable";
import InventoryTopProductsTable from "@/components/inventory/InventoryTopProductsTable";
import InventoryChartsGrid from "@/components/inventory/InventoryChartsGrid";
import InventoryOutboundReport from "@/components/inventory/InventoryOutboundReport";
import NeedsReviewWidget from "@/components/inventory/NeedsReviewWidget";
import ShareInventoryDialog from "@/components/inventory/ShareInventoryDialog";
import HelpTooltip from "@/components/HelpTooltip";

const TABS = [
  { id: "summary",  label: "Resumen por SKU" },
  { id: "pivot",    label: "Pivot mensual" },
  { id: "dead",     label: "Dead stock" },
  { id: "users",    label: "Por usuario" },
  { id: "top",      label: "Top productos" },
  { id: "charts",   label: "Gráficas" },
  { id: "outbound", label: "Reporte de Salidas" },
];

export default function Inventory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, setFilters, reset } = useFilters();
  const [includeNonWarehouse, setIncludeNonWarehouse] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["summary"]));

  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  const inventoryFilters = useMemo(
    () => ({
      ...filters,
      include_non_warehouse: includeNonWarehouse,
    }),
    [filters, includeNonWarehouse]
  );
  const txTypesKey = (filters.transaction_types || []).join(",");

  useEffect(() => {
    let cancelled = false;
    setKpisLoading(true);
    inventory
      .getKpis(inventoryFilters)
      .then((d) => { if (!cancelled) setKpis(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.kpisLoad", { defaultValue: "No se pudieron cargar los KPIs" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setKpisLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.preset, filters.date_from, filters.date_to,
    filters.source_report, txTypesKey,
    includeNonWarehouse,
  ]);

  const handleTabChange = (next) => {
    setActiveTab(next);
    setVisitedTabs((prev) => {
      const ns = new Set(prev);
      ns.add(next);
      return ns;
    });
  };

  return (
    <div className="space-y-4" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Boxes className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("inventory.title", { defaultValue: "Inventario / Movimiento de Productos" })}
            </h1>
            <HelpTooltip anchor="inventory" label={t("inventory.helpAria", { defaultValue: "Ayuda inventario" })} />
          </div>
          <p className="text-sm text-muted-foreground">
            {t("inventory.subtitle", { defaultValue: "Resumen de movimientos por SKU, KPIs de stock y revisión de movimientos ambiguos." })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareOpen(true)}
            data-testid="open-share-inventory-btn"
          >
            <Share2 className="h-3.5 w-3.5 mr-1.5" />
            {t("inventory.share.openBtn", { defaultValue: "Compartir" })}
          </Button>
          <Link to="/catalog" className="inline-flex">
            <Button variant="ghost" size="sm" data-testid="back-to-catalog-btn">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
              {t("inventory.backToCatalog", { defaultValue: "Volver al catálogo" })}
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters panel (collapsible, oculto por default) */}
      <GlobalFilters
        filters={filters}
        onChange={setFilters}
        onReset={reset}
        testId="inventory-filters"
        storageKey="filters_panel_open:inventory"
      />

      {/* Toggle warehouse-only */}
      <div className="flex items-center justify-end gap-2">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-md px-2.5 py-1.5">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <Label htmlFor="warehouse-toggle" className="cursor-pointer">
            {t("inventory.includeNonWarehouse", { defaultValue: "Incluir todos los usuarios (fuera de bodega)" })}
          </Label>
          <Switch
            id="warehouse-toggle"
            checked={includeNonWarehouse}
            onCheckedChange={setIncludeNonWarehouse}
            data-testid="warehouse-toggle"
          />
        </div>
      </div>

      {/* KPIs + Needs Review widget */}
      <div className="grid gap-3 xl:grid-cols-[1fr_280px]">
        <InventoryKPICards data={kpis} loading={kpisLoading} testId="inventory-kpis" />
        <NeedsReviewWidget
          count={kpis?.needs_user_review_count}
          loading={kpisLoading}
          testId="needs-review-widget"
          onReviewClick={() => navigate("/inventory/review")}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} data-testid="inventory-tabs">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-muted/40">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="text-xs h-7 data-[state=active]:bg-card"
              data-testid={`tab-${tab.id}`}
            >
              {t(`inventory.tabs.${tab.id}`, { defaultValue: tab.label })}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="summary" className="mt-3">
          {visitedTabs.has("summary") && (
            <InventorySummaryTable filters={inventoryFilters} testId="inventory-summary-table" />
          )}
        </TabsContent>
        <TabsContent value="pivot" className="mt-3">
          {visitedTabs.has("pivot") && (
            <InventoryMonthlyPivotTable filters={inventoryFilters} testId="monthly-pivot-table" />
          )}
        </TabsContent>
        <TabsContent value="dead" className="mt-3">
          {visitedTabs.has("dead") && (
            <InventoryDeadStockTable filters={inventoryFilters} testId="dead-stock-table" />
          )}
        </TabsContent>
        <TabsContent value="users" className="mt-3">
          {visitedTabs.has("users") && (
            <InventoryUserMovementsTable filters={inventoryFilters} testId="user-movements-table" />
          )}
        </TabsContent>
        <TabsContent value="top" className="mt-3">
          {visitedTabs.has("top") && (
            <InventoryTopProductsTable filters={inventoryFilters} testId="top-products-table" />
          )}
        </TabsContent>
        <TabsContent value="charts" className="mt-3">
          {visitedTabs.has("charts") && (
            <InventoryChartsGrid filters={inventoryFilters} testId="inventory-charts" />
          )}
        </TabsContent>
        <TabsContent value="outbound" className="mt-3">
          {visitedTabs.has("outbound") && (
            <InventoryOutboundReport filters={inventoryFilters} testId="outbound-report" />
          )}
        </TabsContent>
      </Tabs>

      {/* Phase 7.1 — share-link creator / manager */}
      <ShareInventoryDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        currentFilters={filters}
        includeNonWarehouse={includeNonWarehouse}
      />
    </div>
  );
}
